import { useRef } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import backend from '../../services/backend';
import { Card } from '../../components/Card';
import { empiricalConfusability, type ClusterResponse } from './insights';
import { HelpTip } from '../../components/HelpTip';
import { PagePreviewHoverProvider, usePagePreviewHover } from '../../components/pagePreview/usePagePreviewHover';

type Sibling = Awaited<ReturnType<typeof backend.listCategoryClusters>>['items'][number];

// Raw cosine between cluster centroids, which live in the embedding adapter's
// projected space — NOT the normalized page score, so the DR-2073 score
// recalibration does not apply here. The adapter compresses centroid-centroid
// similarities upward: the old 0.85 cutoff (tuned pre-adapter, flagged ~26% of
// clusters) would flag ~86% post-adapter. 0.93 is the quantile-equivalent on
// the 39k-page dev corpus (~27% flagged) (DR-2073).
const NEAR_OVERLAP = 0.93;

function Bar({
  label,
  value,
  max,
  to,
  danger,
  previewClusterId,
}: {
  label: React.ReactNode;
  value: number;
  max: number;
  to?: string;
  danger?: boolean;
  // When set, hovering the row previews that cluster's first member page in the
  // shared popover (same as the Members card).
  previewClusterId?: number;
}) {
  const pct = max > 0 ? (value / max) * 100 : 0;
  const { show, hide } = usePagePreviewHover();
  const queryClient = useQueryClient();
  const hoveringRef = useRef(false);
  const rowRef = useRef<HTMLDivElement>(null);

  const onMouseEnter =
    previewClusterId == null
      ? undefined
      : () => {
          hoveringRef.current = true;
          // Resolve the cluster's first page on demand (shared ['categoryCluster', id]
          // cache). Guard with hoveringRef so a resolve after mouseleave never shows a
          // stale popover, and re-read the row's rect at show time so a scroll during
          // the fetch doesn't anchor it to a stale position.
          queryClient
            .fetchQuery({
              queryKey: ['categoryCluster', String(previewClusterId)],
              queryFn: () => backend.getCategoryCluster(String(previewClusterId)).then((res) => res.data),
              staleTime: 5 * 60 * 1000,
            })
            .then((data) => {
              const first = data.pages[0];
              if (first && hoveringRef.current && rowRef.current) show(first, rowRef.current.getBoundingClientRect());
            })
            .catch(() => {});
        };

  const onMouseLeave =
    previewClusterId == null
      ? undefined
      : () => {
          hoveringRef.current = false;
          hide();
        };

  return (
    <div ref={rowRef} className="flex items-center gap-2 text-sm" onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave}>
      <div className="w-40 shrink-0 truncate text-gray-700 dark:text-zinc-300">{to ? <Link to={to} className="hover:underline">{label}</Link> : label}</div>
      <div className="relative h-3 flex-1 rounded bg-gray-100 dark:bg-zinc-700/40">
        <div className={`absolute inset-y-0 left-0 rounded ${danger ? 'bg-red-500' : 'bg-indigo-500'}`} style={{ width: `${pct}%` }} />
      </div>
      <div className="w-12 shrink-0 text-right tabular-nums text-gray-500 dark:text-zinc-400">{value}</div>
    </div>
  );
}

export function ConfusabilityCard({
  clusterId,
  categoryName,
  pages,
  siblings,
}: {
  clusterId: string;
  categoryName: string | undefined;
  pages: ClusterResponse['pages'];
  siblings: Sibling[];
}) {
  const empirical = empiricalConfusability(pages, categoryName).slice(0, 8);
  const empiricalMax = empirical[0]?.count ?? 0;

  const nearestQuery = useQuery({
    queryKey: ['categoryClusterNearest', clusterId],
    queryFn: () => backend.getCategoryClusterNearest(clusterId, 8),
    enabled: !!clusterId,
  });
  const otherCategoryNearest = (nearestQuery.data?.items ?? []).filter((c) => !c.sameCategory);

  const otherSiblings = siblings.filter((s) => s.id !== Number(clusterId));
  const siblingMax = siblings.reduce((m, s) => Math.max(m, s.pageCount), 0);

  return (
    <Card header={<h2 className="text-lg font-medium text-gray-900 dark:text-zinc-100">Confusability</h2>}>
      <PagePreviewHoverProvider>
        <div className="space-y-6">
        <section>
          <h3 className="mb-2 text-sm font-medium text-gray-900 dark:text-zinc-100">
            Pages here also score as{' '}
            <HelpTip text="Other categories appearing in members' top-5 classifications — where these pages are most often confused, from actual scores." />
          </h3>
          {empirical.length === 0 ? (
            <p className="text-sm text-gray-500 dark:text-zinc-400">No competing categories in members' top-5.</p>
          ) : (
            <div className="space-y-1.5">
              {empirical.map((row) => (
                <Bar key={row.category} label={row.category} value={row.count} max={empiricalMax} />
              ))}
            </div>
          )}
        </section>

        <section>
          <h3 className="mb-2 text-sm font-medium text-gray-900 dark:text-zinc-100">
            Geometrically nearest clusters (other categories){' '}
            <HelpTip text="Other categories' clusters closest to this one in embedding space — confusion risk even for pages not yet in this cluster. Red = near-overlap (>93% similarity in adapter space)." />
          </h3>
          {nearestQuery.isLoading ? (
            <div className="h-16 animate-pulse rounded bg-gray-100 dark:bg-zinc-700/40" />
          ) : otherCategoryNearest.length === 0 ? (
            <p className="text-sm text-gray-500 dark:text-zinc-400">No nearby clusters from other categories.</p>
          ) : (
            <div className="space-y-1.5">
              {otherCategoryNearest.map((c) => (
                <Bar
                  key={c.id}
                  label={`${c.categoryName} · #${c.id}`}
                  value={Math.round(c.similarity * 100)}
                  max={100}
                  to={`/category-clusters/${c.id}`}
                  danger={c.similarity >= NEAR_OVERLAP}
                  previewClusterId={c.id}
                />
              ))}
            </div>
          )}
        </section>

        <section>
          <h3 className="mb-2 text-sm font-medium text-gray-900 dark:text-zinc-100">
            Sibling clusters{categoryName ? ` in ${categoryName}` : ''}{' '}
            <HelpTip text="Other clusters in the same category, by size. Many small siblings can indicate an over-split category." />
          </h3>
          {otherSiblings.length === 0 ? (
            <p className="text-sm text-gray-500 dark:text-zinc-400">This is the only cluster in the category.</p>
          ) : (
            <div className="space-y-1.5">
              {siblings
                .slice()
                .sort((a, b) => b.pageCount - a.pageCount)
                .map((s) => (
                  <Bar
                    key={s.id}
                    label={
                      <span className={s.id === Number(clusterId) ? 'font-semibold text-indigo-600 dark:text-indigo-400' : undefined}>
                        #{s.id}
                        {s.id === Number(clusterId) ? ' (this)' : ''}
                      </span>
                    }
                    value={s.pageCount}
                    max={siblingMax}
                    to={s.id === Number(clusterId) ? undefined : `/category-clusters/${s.id}`}
                    previewClusterId={s.id === Number(clusterId) ? undefined : s.id}
                  />
                ))}
            </div>
          )}
        </section>
        </div>
      </PagePreviewHoverProvider>
    </Card>
  );
}
